import type {
  OrganizationTax,
  ReceiptDetail,
  ReceiptDraftPayload,
  ReceiptEtaStatus,
  ReceiptLineDraft,
  ReceiptPreview,
  ReceiptStatus,
} from '../../api/types';

/**
 * One selectable ETA receipt type (compliance review §9.1, receipt v1.2). `reference` marks the
 * return types that must carry a prior receipt's uuid; `withoutReference` is the RWR return, whose
 * two return fields (documentUseReason, salesIssuedDateTime) are mandatory.
 */
export interface ReceiptTypeOption {
  code: string;
  /** A return with reference: the request needs the returned receipt's uuid. */
  reference: boolean;
  /** Return without reference (RWR): documentUseReason + salesIssuedDateTime are mandatory. */
  withoutReference: boolean;
}

const INDUSTRY = ['C', 'S', 'R', 'T', 'B', 'E', 'P', 'H', 'N', 'U'] as const;

/** The v1.2 receipt type set: the three base types and the ten industry pairs (23 codes). */
export const RECEIPT_TYPES: readonly ReceiptTypeOption[] = [
  { code: 's', reference: false, withoutReference: false },
  { code: 'r', reference: true, withoutReference: false },
  { code: 'RWR', reference: false, withoutReference: true },
  ...INDUSTRY.map((x) => ({ code: `S${x}`, reference: false, withoutReference: false })),
  ...INDUSTRY.map((x) => ({ code: `R${x}`, reference: true, withoutReference: false })),
];

export const RECEIPT_TYPE_CODES: readonly string[] = RECEIPT_TYPES.map((t) => t.code);

export function receiptTypeOption(code: string): ReceiptTypeOption | undefined {
  return RECEIPT_TYPES.find((t) => t.code === code);
}

export function isReturnWithoutReference(code: string): boolean {
  return receiptTypeOption(code)?.withoutReference ?? false;
}

export function isReturnWithReference(code: string): boolean {
  return receiptTypeOption(code)?.reference ?? false;
}

/**
 * The ETA axis values the filter offers, in order. A receipt is Valid, Invalid or Cancelled at ETA
 * (compliance review §9.10), on its way before that; no receiver rejects a receipt, so `rejected` is
 * never offered (WP-8.9: no dead options). One refused at submission is stored Invalid; the
 * `not_accepted` option narrows Invalid to those receipts (RB3R8-04), which the register marks.
 */
export const RECEIPT_ETA_STATUSES: readonly ReceiptEtaStatus[] = [
  'submitting',
  'submitted',
  'valid',
  'invalid',
  'not_accepted',
  'cancelled',
];

/**
 * The receipt stages the list filter offers. The schema also knows `discarded`, but no screen and no
 * API route discards a receipt, so offering it would be a value that can never match (WP-8.9, role
 * 4's §6 note: no dead options).
 */
export const RECEIPT_STATUSES: readonly ReceiptStatus[] = ['draft', 'issued'];

/** Page sizes the receipts list offers: the billing register's set, up to 1000 (MF-RCP-001). */
export const RECEIPT_PAGE_SIZES: readonly number[] = [25, 50, 100, 250, 500, 1000];

export type ReceiptBlocker = 'type' | 'pos' | 'rwrReason' | 'rwrSaleDate' | 'reference';

/**
 * Everything that stops a receipt draft from being saved, in the browser. A draft is otherwise
 * lenient (the server accepts a partial draft), but the POS is mandatory to store it and the return
 * rules are mandatory on the form. The server stays authoritative; this only avoids a dead submit.
 */
export function receiptBlockers(draft: ReceiptDraftPayload): ReceiptBlocker[] {
  const blockers: ReceiptBlocker[] = [];
  if (!draft.receiptType) blockers.push('type');
  if (!draft.posDeviceId) blockers.push('pos');
  if (isReturnWithoutReference(draft.receiptType)) {
    if (!draft.documentUseReason) blockers.push('rwrReason');
    if (!draft.salesIssuedDateTime) blockers.push('rwrSaleDate');
  }
  if (isReturnWithReference(draft.receiptType) && !draft.referenceUUID) blockers.push('reference');
  return blockers;
}

/**
 * The ETA axis shown for a list row (D-1 v2 for receipts): the ETA status once the receipt has been
 * sent, otherwise nothing (a stored draft has no ETA row yet).
 */
export function receiptEtaAxis(row: {
  status: ReceiptStatus;
  etaStatus: ReceiptEtaStatus | null;
}): ReceiptEtaStatus | null {
  if (row.status === 'draft') return null;
  return row.etaStatus ?? null;
}

/** Turns a date ('YYYY-MM-DD') into the ISO datetime the return header expects (noon Cairo, UTC+02). */
export function saleDateToIso(date: string): string {
  return `${date}T12:00:00+02:00`;
}

/** A sale type: `s` or an industry sale `S…` (not a return). */
export function isSaleType(code: string): boolean {
  return code === 's' || /^S[A-Z]$/.test(code);
}

/** The sale type a return with reference points at (`r` → `s`, `RC` → `SC`); null otherwise. */
export function saleTypeFor(returnType: string): string | null {
  if (returnType === 'r') return 's';
  return /^R[A-Z]$/.test(returnType) && returnType !== 'RWR' ? `S${returnType.slice(1)}` : null;
}

/** The return-with-reference type of a sale (`s` → `r`, `SC` → `RC`); null for a non-sale. */
export function returnTypeFor(saleType: string): string | null {
  if (saleType === 's') return 'r';
  return /^S[A-Z]$/.test(saleType) ? `R${saleType.slice(1)}` : null;
}

/**
 * A new draft prefilled from an issued receipt (WP-8.9c), never saved by itself: the user reviews it
 * in the editor and it takes a new number at its first save.
 * - `correction`: the same receipt again (an invalid or not-accepted one, to correct and re-issue);
 * - `return`: a return with reference of a valid sale, pointing at its uuid.
 * The receipt date is not copied (a new receipt is dated when it is issued); line taxes follow each
 * item's defaults, as when a line is picked in the editor.
 */
/**
 * The preview's totals, only when they are the amounts the issue will produce: the engine still
 * computes a draft whose lines are incomplete (a missing price or fee amount counts as 0), and such
 * a sum is not shown as the receipt's amounts (DEC-111). Header issues do not change the amounts.
 */
export function issuableTotals(preview: ReceiptPreview | null | undefined) {
  if (!preview?.totals) return null;
  const linesIncomplete = preview.issues.some(
    (i) => i.path === 'lines' || i.path.startsWith('lines.'),
  );
  return linesIncomplete ? null : preview.totals;
}

/**
 * A stored decimal as a person types it: the trailing zeros of the column's scale dropped
 * ("1.00000" → "1", "12.50000" → "12.5"). The value is unchanged.
 */
export function plainDecimal(value: string): string {
  if (!/^-?\d+\.\d+$/.test(value)) return value;
  return value.replace(/0+$/, '').replace(/\.$/, '');
}

/**
 * The source line's own taxes as this organization's taxes, with each fixed fee's amount (DEC-111),
 * when every one of them is still an active tax of the organization; otherwise nothing, and the
 * line takes its item's default taxes as before.
 */
function sourceTaxes(
  line: ReceiptDetail['lines'][number],
  taxes: Parameters<typeof draftFromReceipt>[2],
): Pick<ReceiptLineDraft, 'taxIds' | 'fixedTaxAmounts'> {
  if (!taxes || !line.taxes.length) return {};
  const taxIds: string[] = [];
  const fixedTaxAmounts: Record<string, string> = {};
  for (const tax of line.taxes) {
    const org = taxes.find(
      (x) => x.isActive && x.taxType === tax.taxType && x.taxSubtype === tax.taxSubtype,
    );
    if (!org) return {};
    if (taxIds.includes(org.id)) continue;
    taxIds.push(org.id);
    if (org.kind === 'fixed') fixedTaxAmounts[org.id] = plainDecimal(tax.amount);
  }
  return Object.keys(fixedTaxAmounts).length ? { taxIds, fixedTaxAmounts } : { taxIds };
}

export function draftFromReceipt(
  source: ReceiptDetail,
  as: 'correction' | 'return',
  taxes?: readonly Pick<OrganizationTax, 'id' | 'taxType' | 'taxSubtype' | 'kind' | 'isActive'>[],
): ReceiptDraftPayload {
  const r = source.receipt;
  const receiptType = as === 'return' ? (returnTypeFor(r.receiptType) ?? 'r') : r.receiptType;
  const draft: ReceiptDraftPayload = {
    receiptType,
    currency: r.currency,
    posDeviceId: r.posDeviceId,
    branchId: r.branchId,
    lines: source.lines.map((l) => ({
      ...(l.itemId ? { itemId: l.itemId } : {}),
      description: l.description,
      ...(l.quantity ? { quantity: plainDecimal(l.quantity) } : {}),
      ...(l.unitPrice ? { unitPrice: plainDecimal(l.unitPrice) } : {}),
      ...sourceTaxes(l, taxes),
    })),
  };
  if (r.exchangeRate) draft.exchangeRate = plainDecimal(r.exchangeRate);
  if (r.paymentMethod) draft.paymentMethod = r.paymentMethod;
  if (r.activityCode) draft.activityCode = r.activityCode;
  const buyer: NonNullable<ReceiptDraftPayload['buyer']> = { type: r.buyerType };
  if (r.buyerId) buyer.id = r.buyerId;
  if (r.buyerName) buyer.name = r.buyerName;
  if (r.buyerMobile) buyer.mobile = r.buyerMobile;
  draft.buyer = buyer;
  if (as === 'return') {
    if (r.uuid) draft.referenceUUID = r.uuid;
  } else {
    if (r.referenceUuid) draft.referenceUUID = r.referenceUuid;
    if (r.documentUseReason === 'I' || r.documentUseReason === 'B')
      draft.documentUseReason = r.documentUseReason;
    if (r.salesIssuedDateTime) draft.salesIssuedDateTime = r.salesIssuedDateTime;
  }
  return draft;
}
